import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  signOut as amplifySignOut,
} from "aws-amplify/auth";

import { OPPOSITE_GROUP, REQUIRED_GROUP } from "../config/cognito";

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export type InitiateResult =
  | { mode: "otp" }
  | { mode: "done" };

export async function initiateOtpSignIn(email: string): Promise<InitiateResult> {
  const username = normalizeEmail(email);

  // Recover from "There is already a signed in user" REACTIVELY (ADR-0036).
  // This used to call `ensureSignedOut()` unconditionally, which meant merely
  // OPENING /login in one tab destroyed the Cognito session for the whole
  // browser profile — and the Resend button did it again. The pre-emptive
  // sign-out only ever existed to dodge this one error, so handle the error.
  let out;
  try {
    out = await amplifySignIn({
      username,
      options: {
        authFlowType: "USER_AUTH",
        preferredChallenge: "EMAIL_OTP",
      },
    });
  } catch (err) {
    if (!isAlreadySignedInError(err)) throw err;
    await ensureSignedOut();
    out = await amplifySignIn({
      username,
      options: {
        authFlowType: "USER_AUTH",
        preferredChallenge: "EMAIL_OTP",
      },
    });
  }
  if (out.isSignedIn) return { mode: "done" };
  if (out.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
    return { mode: "otp" };
  }
  throw new Error(
    `Unexpected Cognito next step: ${out.nextStep?.signInStep ?? "<none>"}`,
  );
}

/**
 * Re-trigger OTP send for the same email — see web-customer for full notes.
 */
export async function resendOtp(email: string): Promise<InitiateResult> {
  return initiateOtpSignIn(email);
}

function isAlreadySignedInError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? "";
  const message = (err as { message?: string } | null)?.message ?? "";
  return (
    name === "UserAlreadyAuthenticatedException" ||
    /already\s+a?\s*signed[- ]in user/i.test(message)
  );
}

async function ensureSignedOut(): Promise<void> {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    if (session.tokens?.accessToken) {
      await amplifySignOut();
    }
  } catch {
    // No session — fine.
  }
}

export async function submitOtp(otp: string): Promise<void> {
  const out = await confirmSignIn({ challengeResponse: otp });
  if (!out.isSignedIn) {
    throw new Error("OTP did not complete sign-in. Please try again.");
  }
}

export async function currentCognitoIdToken(): Promise<string> {
  const session = await fetchAuthSession({ forceRefresh: false });
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("Cognito session has no ID token.");
  return token;
}

export async function refreshCognitoIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function inRequiredCognitoGroup(): Promise<boolean> {
  const session = await fetchAuthSession({ forceRefresh: false });
  const claim = session.tokens?.idToken?.payload["cognito:groups"];
  const groups = Array.isArray(claim)
    ? claim.filter((g): g is string => typeof g === "string")
    : [];
  if (groups.includes(REQUIRED_GROUP)) return true;
  if (groups.includes(OPPOSITE_GROUP)) return false;
  return true;
}

export interface SessionEmail {
  email: string;
}

export async function fetchSessionEmail(): Promise<SessionEmail | null> {
  try {
    const attrs = await fetchUserAttributes();
    const email = attrs.email;
    if (!email) return null;
    return { email: normalizeEmail(email) };
  } catch {
    return null;
  }
}

export interface CognitoSignUpArgs {
  email: string;
  password: string;
  userAttributes?: Record<string, string>;
}

export interface CognitoSignUpResult {
  cognitoSub: string;
  isAutoConfirmed: boolean;
}

export async function cognitoSignUp(
  args: CognitoSignUpArgs,
): Promise<CognitoSignUpResult> {
  const email = normalizeEmail(args.email);
  const out = await amplifySignUp({
    username: email,
    password: args.password,
    options: {
      userAttributes: {
        email,
        ...(args.userAttributes ?? {}),
      },
    },
  });
  const sub = out.userId;
  if (!sub) {
    throw new Error(
      "Cognito did not return a userId on signUp; cannot bind local user record.",
    );
  }
  return {
    cognitoSub: sub,
    isAutoConfirmed: out.isSignUpComplete === true,
  };
}

export async function cognitoSignOut(): Promise<void> {
  try {
    await amplifySignOut();
  } catch {
    // best-effort
  }
}

export function newThrowawayPassword(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/[+/=]/g, "x") + "Aa1!";
}
